import React, { useState } from 'react';
import { Home, Key, Shield, Calculator, ClipboardList, Bot, Users, Mail, Eye, EyeOff } from 'lucide-react';

export default function LandingPage({ onLoginGoogle, onLoginEmail, onSignUpEmail }) {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isSignUp) {
        await onSignUpEmail(email, password, displayName);
      } else {
        await onLoginEmail(email, password);
      }
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/user-not-found' || code === 'auth/invalid-credential') {
        setError('Invalid email or password.');
      } else if (code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Try signing in.');
      } else if (code === 'auth/weak-password') {
        setError('Password must be at least 6 characters.');
      } else if (code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleClick = async () => {
    setError('');
    setLoading(true);
    try {
      await onLoginGoogle();
    } catch (err) {
      console.error('Google login error:', err);
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        // User closed the popup, not an error
      } else if (err.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized for sign-in. Please contact support.');
      } else {
        setError(`Sign-in failed: ${err.code || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <nav style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 32px', background: 'var(--dark)', color: 'white',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%', background: 'var(--primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Key size={18} color="white" />
          </div>
          <span style={{ fontSize: 18, fontWeight: 700 }}>Closing Day Ontario</span>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={handleGoogleClick} style={{
            padding: '10px 24px', background: 'var(--primary)', color: 'white',
            borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14,
            cursor: 'pointer', border: 'none',
          }}>
            Sign In
          </button>
        </div>
      </nav>

      <div style={{
        maxWidth: 1100, margin: '0 auto', padding: '80px 24px', textAlign: 'center',
      }}>
        <h1 style={{
          fontSize: 48, fontWeight: 800, lineHeight: 1.15, marginBottom: 20,
          color: 'var(--dark)',
        }}>
          Your Complete Guide to<br />
          <span style={{ color: 'var(--primary)' }}>Buying Your First Home</span>
        </h1>
        <p style={{
          fontSize: 18, color: 'var(--gray-600)', maxWidth: 600, margin: '0 auto 40px',
          lineHeight: 1.6,
        }}>
          From saving your down payment to getting the keys, Closing Day Ontario walks you through every step of the home buying journey in Ontario.
        </p>

        <div style={{
          maxWidth: 400, margin: '0 auto 16px', display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <button onClick={handleGoogleClick} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            padding: '14px 24px', background: 'white', color: '#333',
            borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 15,
            cursor: 'pointer', border: '1px solid #ddd', boxShadow: 'var(--shadow)',
            width: '100%',
          }}>
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google
          </button>

          {error && !showEmailForm && (
            <p style={{ color: '#dc3545', fontSize: 13, margin: 0, textAlign: 'center' }}>{error}</p>
          )}

          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{ flex: 1, height: 1, background: '#ddd' }} />
            <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>or</span>
            <div style={{ flex: 1, height: 1, background: '#ddd' }} />
          </div>

          {!showEmailForm ? (
            <button onClick={() => setShowEmailForm(true)} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              padding: '14px 24px', background: 'var(--primary)', color: 'white',
              borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 15,
              cursor: 'pointer', border: 'none', boxShadow: 'var(--shadow-md)',
              width: '100%',
            }}>
              <Mail size={18} />
              Continue with Email
            </button>
          ) : (
            <form onSubmit={handleEmailSubmit} style={{
              display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left',
            }}>
              {isSignUp && (
                <input
                  type="text"
                  placeholder="Your name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  style={{
                    padding: '12px 16px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid #ddd', fontSize: 15, width: '100%',
                    boxSizing: 'border-box',
                  }}
                />
              )}
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  padding: '12px 16px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid #ddd', fontSize: 15, width: '100%',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{
                    padding: '12px 16px', paddingRight: 44, borderRadius: 'var(--radius-sm)',
                    border: '1px solid #ddd', fontSize: 15, width: '100%',
                    boxSizing: 'border-box',
                  }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  color: 'var(--gray-500)',
                }}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {error && (
                <p style={{ color: '#dc3545', fontSize: 13, margin: 0 }}>{error}</p>
              )}
              <button type="submit" disabled={loading} style={{
                padding: '14px 24px', background: 'var(--primary)', color: 'white',
                borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 15,
                cursor: loading ? 'not-allowed' : 'pointer', border: 'none',
                opacity: loading ? 0.7 : 1, width: '100%',
              }}>
                {loading ? 'Please wait...' : isSignUp ? 'Create Account' : 'Sign In'}
              </button>
              <p style={{ fontSize: 13, color: 'var(--gray-500)', textAlign: 'center', margin: 0 }}>
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button type="button" onClick={() => { setIsSignUp(!isSignUp); setError(''); }} style={{
                  background: 'none', border: 'none', color: 'var(--primary)',
                  fontWeight: 600, cursor: 'pointer', fontSize: 13, padding: 0,
                }}>
                  {isSignUp ? 'Sign In' : 'Create one'}
                </button>
              </p>
            </form>
          )}
        </div>
      </div>

      <div style={{
        maxWidth: 1100, margin: '0 auto', padding: '0 24px 80px',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24,
      }}>
        {[
          {
            icon: <Calculator size={28} color="var(--primary)" />,
            title: 'Budget Planner',
            desc: 'Calculate your mortgage, CMHC premiums, land transfer tax, and see exactly what you can afford with the stress test.',
          },
          {
            icon: <ClipboardList size={28} color="var(--primary)" />,
            title: 'Inspection Checklist',
            desc: 'Track every item in your home inspection with seasonal and location-specific considerations built in.',
          },
          {
            icon: <Shield size={28} color="var(--primary)" />,
            title: 'Insurance Guide',
            desc: '8 types of insurance explained clearly — from title insurance to sewer backup riders. Know exactly what you need.',
          },
          {
            icon: <Home size={28} color="var(--primary)" />,
            title: 'Move-In Checklist',
            desc: 'Everything from setting up utilities to changing your address. Never miss a step when moving into your new home.',
          },
          {
            icon: <Bot size={28} color="var(--primary)" />,
            title: 'AI Assistant',
            desc: 'Ask anything about buying a home in Ontario. Get personalized, conversational answers powered by AI.',
          },
          {
            icon: <Users size={28} color="var(--primary)" />,
            title: 'Collaboration',
            desc: 'Share your home buying journey with your partner, mortgage broker, or real estate agent.',
          },
        ].map((feature, i) => (
          <div key={i} style={{
            background: 'white', borderRadius: 'var(--radius)', padding: 32,
            boxShadow: 'var(--shadow)', transition: 'box-shadow 0.2s',
          }}>
            <div style={{ marginBottom: 16 }}>{feature.icon}</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: 'var(--dark)' }}>{feature.title}</h3>
            <p style={{ fontSize: 14, color: 'var(--gray-600)', lineHeight: 1.6 }}>{feature.desc}</p>
          </div>
        ))}
      </div>

      <div style={{
        textAlign: 'center', padding: '40px 24px', background: 'var(--dark)', color: 'white',
      }}>
        <p style={{ fontSize: 14, opacity: 0.7 }}>
          Closing Day Ontario — Built for first-time home buyers in Ontario
        </p>
      </div>
    </div>
  );
}
